// Copyright (c) Docugami, Inc. All rights reserved.

import { TokenRange } from "networking/api";
import { Logger, LoggerCategory } from "./Logger";

export interface TokenMargins {
    leftMargin: TokenMargin;
    rightMargin: TokenMargin;
}

export interface TokenMargin {
    textLines: string[];
    boundaryChar: string;
}

export enum TokenType {
    Whitespace = 'S',
    Word = 'W',
    Punctuation = 'P'
}

export interface Token {
    fromChar: number;
    toChar: number;
    text: string;
    type: TokenType;
    whitespaceTypes: WhitespaceTypes;
    index: number
}

enum WhitespaceTypes {
    None = 0,
    NewLine = 1 << 0,
    Tab = 1 << 1,
    Space = 1 << 2
}

/**
 * Utility class for parsing the tokens.txt for a document
 */
export class TokensTextParser {
    private _tokens: Token[];

    constructor(tokens: string | Token[]) {
        if (typeof tokens === 'string') {
            this._tokens = [];
            tokens.split('\n').forEach(entry => {
                if (!!entry) {
                    const token = this.parseToken(entry, this._tokens.length);
                    this._tokens.push(token);
                }
            });
        }
        else {
            this._tokens = tokens;
        }
    }

    get tokens(): Token[] { return this._tokens; }

    /**
     * Gets the whitespace normalized text of all tokens
     * @returns The whitespace normalized text of all tokens
     */
    getNormalizedText(): string {
        let str: string = '';
        let prevToken: Token | undefined;
        this._tokens.forEach(t => {
            str += `${TokensTextParser.hasImpliedSpace(prevToken, t) ? ' ' : ''}${t.type === TokenType.Whitespace ? ' ' : t.text}`;
            prevToken = t;
        });

        return str;
    }

    /**
     * Gets the text for a given token range
     */
    getTextForTokenRange(range: TokenRange, compressSpaces: boolean): string[] {
        const targetRange = this.sanitizeRange(range);

        const targetTokens = this.getTokensForRange(targetRange);
        const tokenLines = this.getTokenLines(targetTokens, false);

        return this.getTextLinesForTokenLines(tokenLines, compressSpaces);
    }

    /**
     * Gets the text content of the margins on either side of a given token range
     */
    getMarginsForTokenRange(range: TokenRange, leftMarginMinSize: number, rightMarginMinSize: number, breakOnNewLine: boolean, compressSpaces: boolean): TokenMargins {
        const targetRange = this.sanitizeRange(range);

        const leftMarginText = this.getLeftMargin(Math.max(0, range.fromTok - 1), leftMarginMinSize, breakOnNewLine, compressSpaces);
        const rightMarginText = this.getRightMargin(targetRange.toTok, rightMarginMinSize, breakOnNewLine, compressSpaces);

        return {
            leftMargin: leftMarginText,
            rightMargin: rightMarginText
        };
    }

    /**
     * For debug use, generate tokens.text again [optionally for sub range]
     */
    toString(fromTok: number = 0, toTok: number = -1): string {
        let res = "";
        if (toTok === -1) {
            toTok = this._tokens.length;
        }

        for (let tokenNum = fromTok; tokenNum < toTok; tokenNum++) {
            const token = this._tokens[tokenNum];
            res += `[${tokenNum}] ${token.fromChar} ${token.toChar} ${token.type} ${token.text}\n`;
        }

        return res;
    }

    /**
     * Gets the margin to the right of the given start token inclusive
     */
    private getRightMargin(marginStartTok: number, minSize: number, breakOnNewLine: boolean, compressSpaces: boolean) {
        return this.getMargin(marginStartTok, minSize, breakOnNewLine, compressSpaces, false);
    }

    /**
     * Gets the margin to the left of the given start token inclusive
     */
    private getLeftMargin(marginStartTok: number, minSize: number, breakOnNewLine: boolean, compressSpaces: boolean) {
        return this.getMargin(marginStartTok, minSize, breakOnNewLine, compressSpaces, true);
    }

    /**
     * Gets the margin given a start token and direction
     */
    private getMargin(marginStartTok: number, minCharCount: number, breakOnNewLine: boolean, compressSpaces: boolean, isDescending: boolean): TokenMargin {
        const tokens = this.getMarginTokens(marginStartTok, minCharCount, isDescending);
        const tokenLines = this.getTokenLines(tokens, breakOnNewLine);

        const marginTextLines = this.getTextLinesForTokenLines(tokenLines, compressSpaces, isDescending);

        let boundaryChar = '';
        const boundaryIndex = isDescending ? marginStartTok + 1 : marginStartTok - 1;
        if (boundaryIndex > 0 && boundaryIndex <= this._tokens.length) {
            // Determine if we need white space on the boundary of the margin
            const curToken = this._tokens[marginStartTok];
            if (TokensTextParser.hasImpliedSpace(curToken, this._tokens[boundaryIndex])) {
                boundaryChar = ' ';
            }
        }

        return {
            textLines: marginTextLines,
            boundaryChar: boundaryChar
        }
    }

    /**
     * Gets the corresponding array of tokens for a given token range
     */
    getTokensForRange(range: TokenRange) {
        let tokens = Array<number>(Math.max(0, range.toTok - range.fromTok));
        for (let i = 0; i < tokens.length; ++i) {
            tokens[i] = i + range.fromTok;
        }

        return tokens;
    }

    /**
     * Gets the array of tokens containing at least the given number of characters.
     */
    private getMarginTokens(startToken: number, minSize: number, descending: boolean) {
        const getNextIndex = (curIndex: number) => descending ? curIndex - 1 : curIndex + 1;

        let accumulator = 0;
        const tokens = [];
        let index = startToken;
        while (accumulator < minSize && index >= 0 && index < this._tokens.length) {
            const token = this._tokens[index];

            // We only guarantee a minimum margin to the nearest token, so it's safe to ignore whitespace
            if (token.type !== TokenType.Whitespace) {
                accumulator += (token.toChar - token.fromChar);
            }

            tokens.push(index);
            index = getNextIndex(index);
        }

        return tokens;
    }

    /**
     * Helper for determining if there is an implicit space between two tokens.
     */
    public static hasImpliedSpace(prevToken: Token | undefined, currentToken: Token) {
        if (!prevToken || prevToken.type === TokenType.Whitespace) {
            return false;
        }

        return !(prevToken.toChar === currentToken.fromChar || prevToken.fromChar === currentToken.toChar);
    }

    /**
     * Converts a collection of token lines into its associated collection of text lines
     */
    private getTextLinesForTokenLines(tokenLines: number[][], compressSpaces: boolean, descending: boolean = false): string[] {
        const textLines: string[] = [];
        for (const tokens of tokenLines) {
            let textLine = this.getTextForTokens(tokens, compressSpaces);
            if (descending) {
                textLine = textLine.reverse();
            }

            textLines.push(textLine.join(''));
        }

        return textLines;
    }

    /**
     * Given an array of tokens, creates an array of strings representing the token content
     */
    private getTextForTokens(tokens: number[], compressSpaces: boolean): string[] {
        let prevToken: Token | undefined = undefined;
        let builder: string[] = [];
        for (const index of tokens) {
            const token = this._tokens[index];

            if (TokensTextParser.hasImpliedSpace(prevToken, token)) {
                builder.push(' ');
            }

            let text = token.text;
            if (token.type === TokenType.Whitespace) {
                const isSpaceType = !(token.whitespaceTypes & WhitespaceTypes.NewLine);
                if (compressSpaces && isSpaceType) {
                    // Compress single line white space to a single space;
                    text = ' ';
                }
            }

            if (!!text) {
                builder.push(text);
            }

            prevToken = token;
        }

        return builder;
    }

    /**
     * Creates an array of tokens for each range of tokens in the input list that are
     * separated by new line whitespace tokens.
     */
    private getTokenLines(tokens: number[], breakOnNewLine: boolean) {
        let tokenLines: number[][] = [];
        let currentLine: number[] = [];
        for (const index of tokens) {
            const token = this._tokens[index];

            if (token.type === TokenType.Whitespace) {
                if (!!(token.whitespaceTypes & WhitespaceTypes.NewLine)) {
                    // Line break
                    tokenLines.push([...currentLine]);
                    currentLine = [];

                    if (breakOnNewLine) {
                        break;
                    }

                    continue;
                }
            }

            currentLine.push(index);
        }

        if (currentLine.length > 0) {
            tokenLines.push([...currentLine]);
        }

        return tokenLines;
    }

    /**
     * Gets a flag indicating the type of whitespace contained in the given whitespace token
     */
    public static getWhitespaceTypes(type: TokenType, text: string) {
        let types: WhitespaceTypes = WhitespaceTypes.None;
        if (type === TokenType.Whitespace) {
            if (text.includes('\\n') || text.includes('\\r')) {
                types |= WhitespaceTypes.NewLine;
            }

            if (text.includes('\\t')) {
                types |= WhitespaceTypes.Tab;
            }

            if (text.includes('\\ ')) {
                types |= WhitespaceTypes.Space;
            }
        }

        return types;
    }

    private sanitizeRange(tokenRange: TokenRange): TokenRange {
        const sanitizedRange = { ...tokenRange };

        sanitizedRange.fromTok = Math.max(0, tokenRange.fromTok);
        sanitizedRange.toTok = Math.min(this._tokens.length, tokenRange.toTok);
        return sanitizedRange;
    }

    /**
     * Parses a single line entry of tokens.txt into it a token
     */
    private parseToken(entry: string, index: number): Token {
        const parts = entry.split(' ', 3);

        // Everything after the token type is the text, the "+2" accounts for type char and the space separator
        const remainingIndex = entry.indexOf(parts[2]) + 2;

        const fromChar = Number.parseInt(parts[0]);
        const toChar = Number.parseInt(parts[1]);
        const type = parts[2] as TokenType;
        // unescaping double quote characters in tokens text
        const text = type === TokenType.Punctuation ? entry.substring(remainingIndex).replace(/\\"/g, '"') : entry.substring(remainingIndex)
        const whitespaceTypes = TokensTextParser.getWhitespaceTypes(type, text);

        // logging tokens that are longer than their token range
        if (type !== TokenType.Whitespace && text.length !== (toChar - fromChar)) {
            Logger.LogWarning(LoggerCategory.Tokens, `Token text length is not equal to character range length for token character range: ${fromChar} to ${toChar}`)
        }

        return {
            fromChar: fromChar,
            toChar: toChar,
            type: type,
            text: text,
            whitespaceTypes: whitespaceTypes,
            index: index
        };
    }
}

export interface TokenRangeForTextRange {
    range: TextRange;
    fromTok: number | null;
    toTok: number | null;
}

export interface TextRange {
    // The starting index of the text
    from: number;

    // The exclusive end index of the text
    to: number;
}

/**
 * Given a list text ranges where each range represents indices in the normalized text of the given token array, returns the associated
 * token ranges for the text.
 * @param tokens List of all tokens used to generate the original normalized text
 * @param ranges The text ranges to map to tokens
 * @returns An array of token ranges and the associated text ranges
 */
export function getTokenRangeForNormalizedTextIndex(tokens: Token[], ranges: TextRange[]): TokenRangeForTextRange[] {
    // Sort the search ranges by end from small -> large
    const searchRanges = [...ranges].sort(sortRangeAscendingTo);

    // Create our results array in the same order
    const results = searchRanges.map((x): TokenRangeForTextRange => { return { range: x, fromTok: null, toTok: null } });

    let startIndex = 0;
    let currentCharCount = 0;
    for (let tokenId = 0; tokenId < tokens.length; ++tokenId) {
        const token = tokens[tokenId];
        const nextToken = tokens[tokenId + 1];

        // We can't rely on the token's fromChar and toChar because they don't reflect the actual number of characters in the escaped string literal.
        // However the document search results are computed from the whitespace-normalized, escaped document text string literal, so we need to
        // track the incremental literal character count for each token, while normalizing the whitespace.
        const fromChar = currentCharCount;
        currentCharCount += !!nextToken && TokensTextParser.hasImpliedSpace(token, nextToken) ? 1 : 0;
        currentCharCount += token.type === TokenType.Whitespace ? 1 : token.text.length;
        const toChar = currentCharCount;

        for (let i = startIndex; i < searchRanges.length; ++i) {
            const currentRange = searchRanges[i];

            // Check to see if the current token contains the from value
            if (!results[i].fromTok && inTokenRange(currentRange.from, fromChar, toChar)) {
                // fromTok hit - calculate offsets and set token value
                results[i].fromTok = tokenId;
            }

            if (!results[i].toTok && inTokenRange(currentRange.to, fromChar, toChar)) {
                // toTok hit - calculate offsets and set token value
                results[i].toTok = tokenId;
            }

            if (currentRange.to > toChar) {
                // Search range is out of our current token range so early out
                break;
            }
        }

        if (!!results[startIndex].fromTok && !!results[startIndex].toTok) {
            // We've finished this item so we don't have to look at it anymore. Since we know the first
            // item will always finish first we just increment the start index.
            ++startIndex
        }

        if (startIndex >= searchRanges.length) {
            // We've finished filling all of the requested ranges
            break;
        }

    }

    return results;
}

function inTokenRange(value: number, from: number, to: number) {
    return value >= from && value < to;
}

function sortRangeAscendingTo(range1: TextRange, range2: TextRange) {
    if (range1.to === range2.to) {
        return 0;
    }

    return range1.to < range2.to ? -1 : 1;
}